"use client";

import { useEffect, useRef } from "react";

/*
 * The story body as blocks: text, and images between them, like an
 * editorial. Saved as blank-line blocks, with each image on its own line as
 * `![description](https://…)`, which the story page renders full width.
 */

export type StoryBlock =
	| { id: string; type: "text"; text: string }
	| {
			id: string;
			type: "image";
			/** A new image, uploaded on save… */
			file?: File;
			/** …or one already saved with a draft. */
			url?: string;
			preview: string;
			alt: string;
	  };

const IMAGE_LINE = /^!\[([^\]\n]*)\]\((https:\/\/[^)\s]+)\)$/;

/** A saved story body back into blocks (text runs and images). */
export function blocksFromBody(body: string): StoryBlock[] {
	const blocks: StoryBlock[] = [];
	for (const part of body.split(/\n\s*\n/)) {
		const text = part.trim();
		if (!text) continue;
		const image = text.match(IMAGE_LINE);
		if (image) {
			blocks.push({
				id: newBlockId(),
				type: "image",
				url: image[2],
				preview: image[2],
				alt: image[1],
			});
			continue;
		}
		const last = blocks[blocks.length - 1];
		if (last?.type === "text") last.text = `${last.text}\n\n${text}`;
		else blocks.push({ id: newBlockId(), type: "text", text });
	}
	if (blocks[blocks.length - 1]?.type !== "text")
		blocks.push({ id: newBlockId(), type: "text", text: "" });
	return blocks;
}

const IMAGE_TYPES = "image/jpeg,image/png,image/gif,image/webp";
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

let counter = 0;
export function newBlockId() {
	counter += 1;
	return `b${Date.now()}-${counter}`;
}

export function storyTextLength(blocks: StoryBlock[]) {
	return blocks.reduce(
		(sum, block) =>
			sum + (block.type === "text" ? block.text.trim().length : 0),
		0,
	);
}

function TextBlock({
	value,
	placeholder,
	onChange,
	disabled,
	autoFocus,
}: {
	value: string;
	placeholder: string;
	onChange: (value: string) => void;
	disabled?: boolean;
	autoFocus?: boolean;
}) {
	const ref = useRef<HTMLTextAreaElement>(null);
	// Grow with what's written, so the whole story stays in view.
	// biome-ignore lint/correctness/useExhaustiveDependencies: resize when the text changes
	useEffect(() => {
		const box = ref.current;
		if (!box) return;
		box.style.height = "auto";
		box.style.height = `${box.scrollHeight}px`;
	}, [value]);
	useEffect(() => {
		if (autoFocus) ref.current?.focus({ preventScroll: false });
	}, [autoFocus]);
	return (
		<textarea
			ref={ref}
			aria-label="Story text"
			className="ed-cmp-story-text"
			rows={3}
			placeholder={placeholder}
			value={value}
			onChange={(event) => onChange(event.target.value)}
			disabled={disabled}
		/>
	);
}

export function StoryBlocksEditor({
	blocks,
	onChange,
	onError,
	disabled,
}: {
	blocks: StoryBlock[];
	onChange: (next: StoryBlock[]) => void;
	onError: (message: string | null) => void;
	disabled?: boolean;
}) {
	const fileRef = useRef<HTMLInputElement>(null);
	const insertAt = useRef(0);
	const focusId = useRef<string | null>(null);

	function insertText(index: number) {
		const block: StoryBlock = { id: newBlockId(), type: "text", text: "" };
		focusId.current = block.id;
		onChange([...blocks.slice(0, index), block, ...blocks.slice(index)]);
	}

	function pickImage(index: number) {
		insertAt.current = index;
		fileRef.current?.click();
	}

	function addImages(files: FileList | null) {
		if (!files?.length) return;
		const picked = [...files];
		const ok = picked.filter((file) => file.size <= MAX_IMAGE_SIZE);
		onError(
			ok.length < picked.length ? "Images must be 5 MB or smaller." : null,
		);
		if (!ok.length) return;
		const images: StoryBlock[] = ok.map((file) => ({
			id: newBlockId(),
			type: "image",
			file,
			preview: URL.createObjectURL(file),
			alt: "",
		}));
		const index = insertAt.current;
		const next = [...blocks.slice(0, index), ...images, ...blocks.slice(index)];
		// Keep somewhere to carry on writing after an image at the end.
		if (index >= blocks.length) {
			const after: StoryBlock = { id: newBlockId(), type: "text", text: "" };
			focusId.current = after.id;
			next.push(after);
		}
		onChange(next);
	}

	function update(id: string, patch: Partial<{ text: string; alt: string }>) {
		onChange(
			blocks.map((block) =>
				block.id === id ? ({ ...block, ...patch } as StoryBlock) : block,
			),
		);
	}

	function remove(id: string) {
		const gone = blocks.find((block) => block.id === id);
		if (gone?.type === "image" && gone.file) URL.revokeObjectURL(gone.preview);
		const next = blocks.filter((block) => block.id !== id);
		onChange(
			next.length ? next : [{ id: newBlockId(), type: "text", text: "" }],
		);
	}

	const insert = (index: number, label: string) => (
		<div className="ed-cmp-insert" data-where={label}>
			<button
				type="button"
				className="ed-button-text ed-meta"
				onClick={() => pickImage(index)}
				disabled={disabled}
			>
				+ Image
			</button>
			<button
				type="button"
				className="ed-button-text ed-meta"
				onClick={() => insertText(index)}
				disabled={disabled}
			>
				+ Text
			</button>
		</div>
	);

	return (
		<div className="ed-cmp-story">
			{blocks.map((block, index) => (
				<div key={block.id} className="ed-cmp-story__block">
					{index > 0
						? insert(index, `Add between blocks ${index} and ${index + 1}`)
						: null}
					{block.type === "text" ? (
						<div className="ed-cmp-story__text">
							<TextBlock
								value={block.text}
								placeholder={
									index === 0
										? "Write the story. Leave a blank line between paragraphs."
										: "Carry on writing"
								}
								onChange={(text) => update(block.id, { text })}
								disabled={disabled}
								autoFocus={focusId.current === block.id}
							/>
							{blocks.length > 1 && !block.text.trim() ? (
								<button
									type="button"
									className="ed-button-text ed-meta ed-secondary"
									onClick={() => remove(block.id)}
									disabled={disabled}
								>
									Remove
								</button>
							) : null}
						</div>
					) : (
						<figure className="ed-cmp-story__figure">
							<img src={block.preview} alt={block.alt} />
							<figcaption className="ed-row" style={{ gap: 16 }}>
								<span className="ed-field" style={{ flex: "1 1 auto" }}>
									<input
										aria-label="Describe the image"
										placeholder="Describe the image (for people who can’t see it)"
										value={block.alt}
										maxLength={200}
										onChange={(event) =>
											update(block.id, { alt: event.target.value })
										}
										disabled={disabled}
									/>
								</span>
								<button
									type="button"
									className="ed-button-text ed-ui ed-secondary"
									onClick={() => remove(block.id)}
									disabled={disabled}
								>
									Remove
								</button>
							</figcaption>
						</figure>
					)}
				</div>
			))}
			{insert(blocks.length, "Add at the end")}
			<input
				ref={fileRef}
				type="file"
				accept={IMAGE_TYPES}
				multiple
				hidden
				onChange={(event) => {
					addImages(event.target.files);
					event.target.value = "";
				}}
			/>
		</div>
	);
}
